import clsx from "clsx";
import { RotateCcw } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

import { useRecentEventForCurrentMode } from "../../../hooks/useRecentEventForCurrentMode";
import { setTrackingState } from "../../../store/slices/socket";
import { selectFocusedEvent } from "../../../store/slices/socket/selectors";
import { resetTiles } from "../../../store/slices/tiles";
import { setModalState, setQuickTileChange } from "../../../store/slices/view";
import type { RootState } from "../../../store/store";
import { eventCleaner } from "../../../utils/eventCleaner";
import { queueEventCleaner } from "../../../utils/queueEventCleaner";
import { getTileDisplayLabel } from "../../../utils/tileDisplayLabels";
import { tileUnits } from "../../../utils/tileUnits";
import { DataTile } from "../DataTile";

interface TilesContainerProps {
  direction?: "row" | "column" | "grid";
  tileType: string;
  maxTiles?: number;
}

type TileKey =
  | "tileOne"
  | "tileTwo"
  | "tileThree"
  | "tileFour"
  | "tileFive"
  | "tileSix"
  | "tileSeven"
  | "tileEight"
  | "tileNine";

const TILE_KEYS: TileKey[] = [
  "tileOne",
  "tileTwo",
  "tileThree",
  "tileFour",
  "tileFive",
  "tileSix",
  "tileSeven",
  "tileEight",
  "tileNine",
];

/**
 * TilesContainer - Displays data tiles for game metrics
 *
 * Shows 9 configurable tiles displaying pitch/hit metrics:
 * - Velocity, Spin Rate, Release Height, etc. (pitching)
 * - Exit Velocity, Launch Angle, Distance, etc. (hitting)
 *
 * Features:
 * - Responsive layout (row vs column mode)
 * - Pulls tile configuration from Redux tiles state
 * - Displays real-time data from recent events
 * - Clickable tiles (will open change modal in Phase 7)
 */
export const TilesContainer = ({
  direction = "row",
  tileType,
  maxTiles,
}: TilesContainerProps) => {
  const dispatch = useDispatch();
  const isRow = direction === "row";
  const isGrid = direction === "grid";
  const isColumn = direction === "column";

  const { isViewingPreviousEvent } = useSelector(
    (state: RootState) => state.socket,
  );
  const focusedEvent = useSelector(selectFocusedEvent);
  const recentEvent = useRecentEventForCurrentMode();

  // Get tiles configuration from Redux
  const tiles = useSelector((state: RootState) => {
    // Map tileType to Redux state key
    if (tileType === "pitching") return state.tiles.pitchingTiles;
    if (tileType === "hitting") return state.tiles.hittingTiles;
    return state.tiles.gameTiles; // default
  });

  // Get event data (either recent or focused previous event)
  const eventData = isViewingPreviousEvent
    ? queueEventCleaner(focusedEvent)
    : eventCleaner(recentEvent);

  // Get data for a specific tile
  const getTileData = (tileKey: TileKey) => {
    const tile = tiles[tileKey];
    if (!tile || tile.type === "visualization") return undefined;
    return eventData?.find((elem) => elem.title === tile.title);
  };

  const areTilesHidden = TILE_KEYS.some((key) => tiles[key]?.hidden);

  const handleTileClick = (tileKey: TileKey) => {
    const tile = tiles[tileKey];
    dispatch(
      setQuickTileChange({
        label: getTileDisplayLabel(tile.title) || tileKey,
        tileType: tileType || "game",
        tileKey,
        placeholder: tile.title || tile.visualization || "",
      }),
    );
    dispatch(setModalState({ state: "Tile Quick Change", data: {} }));
    dispatch(setTrackingState(false));
  };

  return (
    <div className={`relative w-full ${isColumn ? "h-full" : ""}`}>
      {areTilesHidden && (
        <button
          type="button"
          title="Reset Tiles"
          className={clsx(
            // Size
            "h-6 w-6",
            // Layout
            "absolute -right-3 -top-3 z-10 flex items-center justify-center",
            // Border
            "rounded",
            // Background
            "bg-white",
            // States
            "cursor-pointer hover:bg-[hsla(218,100%,52%,0.05)]",
          )}
          onClick={() => dispatch(resetTiles(tileType))}
        >
          <RotateCcw size={14} />
        </button>
      )}
      <div
        className={clsx(
          "w-full",
          isRow && "flex flex-row flex-wrap justify-around gap-4 py-3",
          isColumn && "flex h-full flex-col gap-4",
          isGrid && "grid grid-cols-3 gap-4",
        )}
      >
        {TILE_KEYS.slice(0, maxTiles ?? TILE_KEYS.length).map((tileKey) => {
          const tile = tiles[tileKey];
          if (!tile || tile.hidden || tile.type === "visualization")
            return null;

          const data = getTileData(tileKey);

          return (
            <DataTile
              key={tileKey}
              title={getTileDisplayLabel(tile.title)}
              data={data?.value}
              units={tileUnits[tile.title || ""]}
              onClick={() => handleTileClick(tileKey)}
              rowContainer={isRow || isGrid}
            />
          );
        })}
      </div>
    </div>
  );
};
